import React, { useState, useEffect, useRef } from 'react';
import { authAPI, posAPI } from '../services/api';
import Calculator from './Calculator';
import OrderPanel from './OrderPanel';
import ProductSearch from './ProductSearch';
import Header from './Header';
import ShiftManager from './ShiftManager';
import BarcodeScanner from './BarcodeScanner';
import ProfileModal from './ProfileModal';
import ChangePasswordModal from './ChangePasswordModal';
import { printBill } from '../utils/billPrinter';
import './POSDashboard.css';

const POSDashboard = ({ userData, onLogout, toggleTheme, theme }) => {
  const [currentOrder, setCurrentOrder] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [currentShift, setCurrentShift] = useState(userData.current_shift);
  const [showProfile, setShowProfile] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [heldOrders, setHeldOrders] = useState([]);
  const [productSearchKey, setProductSearchKey] = useState(0); // Add key to force reload
  const productSearchRef = useRef();

  // Load held orders from localStorage on component mount
  useEffect(() => {
    const savedHeldOrders = localStorage.getItem('heldOrders');
    if (savedHeldOrders) {
      setHeldOrders(JSON.parse(savedHeldOrders));
    }

    // Listen for storage changes (from other tabs/windows)
    const handleStorageChange = () => {
      const updatedHeldOrders = localStorage.getItem('heldOrders');
      if (updatedHeldOrders) {
        setHeldOrders(JSON.parse(updatedHeldOrders));
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  useEffect(() => {
    // Calculate total whenever order changes
    const total = currentOrder.reduce((sum, item) => {
      return sum + (item.price * item.quantity);
    }, 0);
    setTotalAmount(total);
  }, [currentOrder]);

  // Function to reload product search
  const reloadProductSearch = () => {
    // Method 1: Using ref to call loadProducts directly
    if (productSearchRef.current && productSearchRef.current.loadProducts) {
      productSearchRef.current.loadProducts();
    }
    
    // Method 2: Force re-render by changing key
    setProductSearchKey(prev => prev + 1);
    
    console.log('Reloading product search...');
  };

  const addToOrder = (product) => {
    setCurrentOrder(prev => {
      // Create unique identifier for the product
      const productId = product.variant ? `variant_${product.variant.id}` : product.id.toString();

      const existingItem = prev.find(item => item.id === productId);

      if (existingItem) {
        return prev.map(item =>
          item.id === productId
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      } else {
        return [...prev, {
          ...product,
          id: productId, // Use the unique identifier
          quantity: 1
        }];
      }
    });
  };

  const updateQuantity = (productId, newQuantity) => {
    if (newQuantity <= 0) {
      removeFromOrder(productId);
      return;
    }

    setCurrentOrder(prev =>
      prev.map(item =>
        item.id === productId
          ? { ...item, quantity: newQuantity }
          : item
      )
    );
  };

  const removeFromOrder = (productId) => {
    setCurrentOrder(prev => prev.filter(item => item.id !== productId));
  };

  const clearOrder = () => {
    setCurrentOrder([]);
  };

  // Handle holding an order
  const handleHoldOrder = (customerName) => {
    const newHeldOrder = {
      id: Date.now().toString(),
      customerName,
      items: [...currentOrder],
      totalAmount,
      timestamp: new Date().toISOString()
    };
    
    const updatedHeldOrders = [...heldOrders, newHeldOrder];
    setHeldOrders(updatedHeldOrders);
    localStorage.setItem('heldOrders', JSON.stringify(updatedHeldOrders));
    clearOrder(); // Clear current order after holding
  };

  // Handle loading a held order
  const handleLoadHeldOrder = (heldOrder) => {
    // Check if there are items in current order before loading
    if (currentOrder.length > 0) {
      const confirmLoad = window.confirm(
        'You have items in the current order. Loading a held order will replace them. Continue?'
      );
      if (!confirmLoad) return;
    }
    
    // Set the held order items as current order
    setCurrentOrder(heldOrder.items);
    
    // Remove from held orders
    const updatedHeldOrders = heldOrders.filter(order => order.id !== heldOrder.id);
    setHeldOrders(updatedHeldOrders);
    localStorage.setItem('heldOrders', JSON.stringify(updatedHeldOrders));
  };

  const handleCheckout = async (paymentData) => {
    try {
      console.log('Processing payment data:', paymentData);

      let orderData;

      if (paymentData.payment_method === 'split') {
        orderData = {
          items: paymentData.items,
          total_amount: paymentData.total_amount,
          payment_method: 'split',
          cash_amount: paymentData.cash_amount,
          card_amount: paymentData.card_amount,
          tender_amount: paymentData.tender_amount,
          change_amount: paymentData.change_amount,
          notes: paymentData.notes,
          shift_id: currentShift?.id,
          // Card details for split payment
          transaction_id: paymentData.transaction_id,
          card_last_four: paymentData.card_last_four,
          card_brand: paymentData.card_brand,
          split_transaction_id: paymentData.transaction_id
        };
      } else {
        orderData = {
          items: paymentData.items,
          total_amount: paymentData.total_amount,
          payment_method: paymentData.payment_method,
          tender_amount: paymentData.tender_amount || paymentData.total_amount,
          change_amount: paymentData.change_amount || 0,
          notes: paymentData.notes,
          shift_id: currentShift?.id
        };

        // Add card details for card payments
        if (paymentData.payment_method === 'card') {
          orderData.transaction_id = paymentData.transaction_id;
          orderData.card_last_four = paymentData.card_last_four;
          orderData.card_brand = paymentData.card_brand;
        }

        // Add transaction ID for other electronic payments
        if (['bank_transfer', 'mobile'].includes(paymentData.payment_method)) {
          orderData.transaction_id = paymentData.transaction_id;
        }
      }

      console.log('Sending order data to API:', orderData);

      // Call the API to create order
      const response = await posAPI.createOrder(orderData);

      console.log('Order created successfully:', response);

      // Extract order data from response
      const orderResponse = response.data.data;
      const receiptNumber = orderResponse.receipt_number || orderResponse.order?.order_number || `REC-${Date.now()}`;

      // Print bill using receipt number from backend
      printBill(currentOrder, paymentData, { receipt_number: receiptNumber });

      // Clear order after successful checkout
      clearOrder();
      
      // Reload product search to refresh stock quantities
      reloadProductSearch();
      
      // Show success message
      // alert('Order processed successfully! Bill printed.');

    } catch (error) {
      console.error('Checkout error:', error);
      let errorMessage = 'Checkout failed! Please try again.';
      
      if (error.response) {
        // Server responded with error status
        errorMessage = error.response.data.message || errorMessage;
      } else if (error.request) {
        // Request was made but no response received
        errorMessage = 'Network error! Please check your connection.';
      }
      
      alert(errorMessage);
    }
  };

  const handleShiftUpdate = (shiftData) => {
    setCurrentShift(shiftData);
  };

  const handleLogout = async () => {
    try {
      await authAPI.logout();
      onLogout();
    } catch (error) {
      console.error('Logout error:', error);
      // Force logout even if API fails
      onLogout();
    }
  };

  return (
    <div className="pos-dashboard">
      <Header
        userData={userData}
        onLogout={handleLogout}
        onProfileClick={() => setShowProfile(true)}
        onChangePasswordClick={() => setShowChangePassword(true)}
        toggleTheme={toggleTheme}
        theme={theme}
      />

      <div className="pos-main">
        {/* Left Panel - Barcode Scanner & Product Search */}
        <div className="pos-left-panel">
          <BarcodeScanner onProductScanned={addToOrder} />
          <ProductSearch 
            key={productSearchKey} // Add key to force re-render
            ref={productSearchRef} // Add ref to call methods directly
            onProductSelect={addToOrder} 
            order={currentOrder}
            onReload={reloadProductSearch} // Pass reload function as prop
          />
        </div>

        {/* Right Panel - Order Panel */}
        <div className="pos-right-panel">
          <OrderPanel
            order={currentOrder}
            onUpdateQuantity={updateQuantity}
            onRemoveItem={removeFromOrder}
            onClearOrder={clearOrder}
            totalAmount={totalAmount}
            onCheckout={handleCheckout}
            onHoldOrder={handleHoldOrder}
            onLoadHeldOrder={handleLoadHeldOrder}
            heldOrders={heldOrders}
          />
        </div>
      </div>

      {/* Modals */}
      <ProfileModal
        isOpen={showProfile}
        onClose={() => setShowProfile(false)}
      />

      <ChangePasswordModal
        isOpen={showChangePassword}
        onClose={() => setShowChangePassword(false)}
      />
    </div>
  );
};

export default POSDashboard;